import { msalInstance, loginRequest, apiConfig } from '../config/authConfig'

function forceLogout() {
  msalInstance.setActiveAccount(null)
  sessionStorage.clear()
  window.location.href = '/login'
}

async function getAccessToken(): Promise<string> {
  // Wait for MSAL to be initialized
  await msalInstance.initialize()
  
  const accounts = msalInstance.getAllAccounts()
  
  if (accounts.length === 0) {
    forceLogout()
    throw new Error('No authenticated user')
  }
  
  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    })
    // Use idToken for API calls (ID token contains user claims)
    return response.idToken
  } catch {
    try {
      const response = await msalInstance.acquireTokenPopup(loginRequest)
      return response.idToken
    } catch {
      forceLogout()
      throw new Error('Token acquisition failed')
    }
  }
}

class ApiClient {
  readonly baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  async getToken(): Promise<string> {
    return getAccessToken()
  }

  async request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    let token: string
    try {
      token = await getAccessToken()
    } catch (tokenErr) {
      console.error('[API] Token acquisition failed:', tokenErr)
      throw tokenErr
    }

    const url = `${this.baseUrl}${path}`
    console.log(`[API] ${method} ${url}`)

    let response: Response
    try {
      response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
      })
    } catch (fetchErr) {
      console.error(`[API] Network error for ${method} ${url}:`, fetchErr)
      throw fetchErr
    }

    if (!response.ok) {
      // Only force logout on 401 (session expired), not on 403 (permission denied)
      if (response.status === 401) {
        console.error('Authentication error (401), forcing logout')
        forceLogout()
        throw new ApiError(response.status, 'AUTH_ERROR', { message: 'Session expired' })
      }
      
      const error = await response.json().catch(() => ({}))
      throw ApiError.fromResponse(response.status, error)
    }

    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return null as T
    }
    return response.json()
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path)
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body)
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body)
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, body)
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path)
  }

  /**
   * GET that returns the raw Response (for reading headers like X-Total-Count).
   */
  async getRaw(path: string): Promise<Response> {
    const token = await getAccessToken()
    const url = `${this.baseUrl}${path}`
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      if (response.status === 401) {
        forceLogout()
        throw new ApiError(response.status, 'AUTH_ERROR', { message: 'Session expired' })
      }
      const error = await response.json().catch(() => ({}))
      throw ApiError.fromResponse(response.status, error)
    }
    return response
  }
}

export class ApiError extends Error {
  status: number
  code: string
  details?: Record<string, unknown>

  constructor(
    status: number,
    code: string,
    details?: Record<string, unknown>,
    message?: string,
  ) {
    super(message || code)
    this.status = status
    this.code = code
    this.details = details
  }

  /**
   * Build from a FastAPI error body. `detail` is either a plain string
   * (HTTPException(detail="...")), an object with `code` (+ optional
   * `message`), or a validation error list. The human-readable text is kept
   * as `message` so pages can show it instead of a bare code.
   */
  static fromResponse(status: number, body: unknown): ApiError {
    const b = (body ?? {}) as { detail?: unknown; error?: { code?: string; message?: string; details?: Record<string, unknown> } }
    const detail = b.detail
    if (typeof detail === 'string') {
      return new ApiError(status, 'UNKNOWN_ERROR', { message: detail }, detail)
    }
    if (Array.isArray(detail)) {
      const text = detail
        .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : String(d)))
        .join('; ')
      return new ApiError(status, 'VALIDATION_ERROR', { message: text }, text)
    }
    const d = (detail ?? undefined) as { code?: string; message?: string } | undefined
    const code = d?.code || b.error?.code || 'UNKNOWN_ERROR'
    const message = d?.message || b.error?.message
    return new ApiError(status, code, (detail as Record<string, unknown>) || b.error?.details, message)
  }
}

export const apiClient = new ApiClient(apiConfig.baseUrl)
